import React, { useEffect, useState } from "react";
import { Spin, Table, Tag, Progress } from "antd";
import { getAdminDashboard } from "../../apis/roleDashboard";

interface AdminData {
  bedOccupancy: {
    total: number;
    occupied: number;
    available: number;
    occupancyPct: number;
    byRoomType: { name: string; total: number; occupied: number; available: number }[];
  };
  revenue: { today: number; yesterday: number; mtd: number };
  outstanding: {
    totalAmount: number;
    count: number;
    topDues: { patientName: string; due: number; invoices: number }[];
  };
  footfall: { today: number; yesterday: number; mtd: number; activeIpd: number };
}

const fmt = (n: number) =>
  n?.toLocaleString("en-IN", { maximumFractionDigits: 0 }) ?? "—";

const AdminDashboard: React.FC = () => {
  const [data, setData] = useState<AdminData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAdminDashboard()
      .then((res: any) => setData(res.data.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center mt-16"><Spin size="large" /></div>;
  if (!data) return <div className="text-gray-400 mt-8 text-center">Failed to load dashboard.</div>;

  const roomTypeColumns = [
    { title: "Room Type", dataIndex: "name", key: "name" },
    { title: "Total", dataIndex: "total", key: "total" },
    { title: "Occupied", dataIndex: "occupied", key: "occupied", render: (v: number) => <Tag color="red">{v}</Tag> },
    { title: "Available", dataIndex: "available", key: "available", render: (v: number) => <Tag color="green">{v}</Tag> },
  ];

  const duesColumns = [
    { title: "Patient", dataIndex: "patientName", key: "patientName" },
    { title: "Invoices", dataIndex: "invoices", key: "invoices" },
    {
      title: "Due (₹)",
      dataIndex: "due",
      key: "due",
      render: (v: number) => <span className="font-semibold text-red-600">₹{fmt(v)}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Today's Revenue</p>
          <p className="text-2xl font-bold text-green-600">₹{fmt(data.revenue.today)}</p>
          <p className="text-xs text-gray-400 mt-1">Yesterday ₹{fmt(data.revenue.yesterday)}</p>
          <p className="text-xs text-gray-400">MTD ₹{fmt(data.revenue.mtd)}</p>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Outstanding Dues</p>
          <p className="text-2xl font-bold text-red-500">₹{fmt(data.outstanding.totalAmount)}</p>
          <p className="text-xs text-gray-400 mt-1">{data.outstanding.count} invoice(s)</p>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Today's Footfall</p>
          <p className="text-2xl font-bold text-blue-600">{data.footfall.today}</p>
          <p className="text-xs text-gray-400 mt-1">Yesterday {data.footfall.yesterday}</p>
          <p className="text-xs text-gray-400">MTD {data.footfall.mtd}</p>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Active IPD</p>
          <p className="text-2xl font-bold text-purple-600">{data.footfall.activeIpd}</p>
          <p className="text-xs text-gray-400 mt-1">
            {data.bedOccupancy.occupied}/{data.bedOccupancy.total} beds occupied
          </p>
        </div>
      </div>

      {/* Bed occupancy */}
      <div className="bg-white rounded-lg p-4 shadow-sm border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-700">Bed Occupancy</h3>
          <span className="text-sm text-gray-500">{data.bedOccupancy.occupancyPct}% occupied</span>
        </div>
        <Progress
          percent={data.bedOccupancy.occupancyPct}
          strokeColor={data.bedOccupancy.occupancyPct > 80 ? "#ef4444" : "#3b82f6"}
          showInfo={false}
          className="mb-4"
        />
        <Table
          dataSource={data.bedOccupancy.byRoomType}
          columns={roomTypeColumns}
          rowKey="name"
          size="small"
          pagination={false}
        />
      </div>

      {/* Top outstanding dues */}
      <div className="bg-white rounded-lg p-4 shadow-sm border">
        <h3 className="font-semibold text-gray-700 mb-3">Top Outstanding Dues</h3>
        <Table
          dataSource={data.outstanding.topDues}
          columns={duesColumns}
          rowKey="patientName"
          size="small"
          pagination={false}
        />
      </div>
    </div>
  );
};

export default AdminDashboard;
